import { useCallback } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import {
    ConnectionStatusFilter,
    CustomerNotificationStatus,
    FlightCheckedInStatus,
    loadDepartureBoardFlights,
    setArrivalAirportFilter,
    setDepartureAirportFilter,
    setCheckInStatusFilter,
    setConnectionStatusFilter,
    setHaulTypeFilter,
    FlightHaulType,
    setDepartureStatusFilter,
    setDelayedBaggageStatusFilter,
    setMisconnectedCustomersStatusFilter,
    setQueryText,
    showHideFilter,
    resetFilters,
    setCurrentPage,
} from '../../../store/features/departureBoard/departureBoardSlice';
import { InputOnChangeData } from '@fluentui/react-components';
import { FlightDepartureStatus } from '../../../model';

interface UseDepartureBoardFiltersParams {
    open: boolean;
    queryText: string;
    loadFlightsDebounced: {
        (page?: number): void;
        cancel: () => void;
    };
}

export const useDepartureBoardFilters = ({
    open,
    queryText,
    loadFlightsDebounced,
}: UseDepartureBoardFiltersParams) => {
    const dispatch = useAppDispatch();

    const onQueryTextChange = useCallback(
        (_: unknown, data: InputOnChangeData) => {
            dispatch(setQueryText(data.value));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onHaulTypeChange = useCallback(
        (haulType: FlightHaulType) => {
            dispatch(setHaulTypeFilter(haulType));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onClearSearchTextClick = useCallback(() => {
        if (!!queryText) {
            loadFlightsDebounced.cancel();
            dispatch(setQueryText(''));
            dispatch(setCurrentPage(1));
            dispatch(loadDepartureBoardFlights());
        }
    }, [dispatch, loadFlightsDebounced, queryText]);

    const onFilterClick = useCallback(() => {
        dispatch(showHideFilter(!open));
    }, [dispatch, open]);

    const onClearFiltersClick = useCallback(() => {
        loadFlightsDebounced.cancel();
        dispatch(resetFilters());
        dispatch(setCurrentPage(1));
        dispatch(loadDepartureBoardFlights());
    }, [dispatch, loadFlightsDebounced]);

    const onDpartureStatusFilterChange = useCallback(
        (status: FlightDepartureStatus, checked: boolean) => {
            dispatch(
                setDepartureStatusFilter({
                    value: status,
                    checked,
                })
            );
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onDepartureAirportFilterChange = useCallback(
        (airport: string, checked: boolean) => {
            dispatch(
                setDepartureAirportFilter({
                    value: airport,
                    checked,
                })
            );
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onArrivalAirportFilterChange = useCallback(
        (airport: string, checked: boolean) => {
            dispatch(
                setArrivalAirportFilter({
                    value: airport,
                    checked,
                })
            );
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onCheckInStatusChange = useCallback(
        (status: FlightCheckedInStatus) => {
            dispatch(setCheckInStatusFilter(status));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onDelayedBaggageStatusChange = useCallback(
        (status: CustomerNotificationStatus) => {
            dispatch(setDelayedBaggageStatusFilter(status));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onMisconnectedCustomersStatusChange = useCallback(
        (status: CustomerNotificationStatus) => {
            dispatch(setMisconnectedCustomersStatusFilter(status));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    const onConnectionStatusChange = useCallback(
        (status: ConnectionStatusFilter) => {
            dispatch(setConnectionStatusFilter(status));
            loadFlightsDebounced(1);
        },
        [dispatch, loadFlightsDebounced]
    );

    return {
        onQueryTextChange,
        onClearSearchTextClick,
        onFilterClick,
        onClearFiltersClick,
        onDpartureStatusFilterChange,
        onDepartureAirportFilterChange,
        onArrivalAirportFilterChange,
        onCheckInStatusChange,
        onDelayedBaggageStatusChange,
        onMisconnectedCustomersStatusChange,
        onConnectionStatusChange,
        onHaulTypeChange,
    };
};
